import type { gridFilters } from '@simplywallst/services';
import { PRIMARY_INDUSTRIES } from '@components/StockScreener/constants/industries';
import {
  formatterCurrency,
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';
import { getBestValuationRatio } from '@/utilities/getBestValuationRatio';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';

type GridResponse = Awaited<
  ReturnType<typeof gridFilters>
>['data']['data'][number];

type StatementsResponse = GridResponse['statements'];

type StatementValue = 'Pass' | 'Fail' | 'No data';

interface Statement {
  name: string;
  title: string;
  description: string;
  formattedDescription: string;
  context: Record<string, string>;
  value: StatementValue;
  severity: 'minor' | 'major' | 'none';
  question: string;
  outcomeName: string;
}

type Statements = Record<string, Statement>;

export interface Company {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  mainThumb: string;
  exchangeSymbol: string;
  shortName: string;
  yearFounded: string;
  logoUrl: string;
  legalName: string;
  slug: string;
  snowflakePoints: number[];
  tickerSymbol: string;
  uniqueSymbol: string;
  return1D: number | null;
  return1DFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCap: number | null;
  marketCapFormatted: string;
  sharePrice: number | null;
  sharePriceFormatted: string;
  analystsPriceTarget: number | null;
  analystsPriceTargetFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  analystCount: number | null;
  growthEstimate: number | null;
  growthEstimateFormatted: string;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
  salesGrowth: number | null;
  salesGrowthFormatted: string;
  dividendYield: number | null;
  dividendYieldFormatted: string;
  industrySlug: string;
  industryLabel: string;
  riskKeys: string[];
  rewardKeys: string[];
  statements: Statements;
  hasRisks: boolean;
  hasRewards: boolean;
}

const getDefaultCompany = (): Company => ({
  canonicalUrl: 'n/a',
  companyId: 'n/a',
  shortDescription: 'n/a',
  mainThumb: 'n/a',
  exchangeSymbol: 'n/a',
  shortName: 'n/a',
  yearFounded: 'n/a',
  logoUrl: 'n/a',
  legalName: 'n/a',
  slug: 'n/a',
  snowflakePoints: [],
  tickerSymbol: 'n/a',
  uniqueSymbol: 'n/a',
  return1D: null,
  return1DFormatted: 'n/a',
  return7D: null,
  return7DFormatted: 'n/a',
  return1YrAbs: null,
  return1YrAbsFormatted: 'n/a',
  marketCap: null,
  marketCapFormatted: 'n/a',
  sharePrice: null,
  sharePriceFormatted: 'n/a',
  analystsPriceTarget: null,
  analystsPriceTargetFormatted: 'n/a',
  bestValuationRatio: {
    valuationType: 'n/a',
    valuation: null,
    valuationFormatted: 'n/a',
  },
  peFormatted: 'n/a',
  pbFormatted: 'n/a',
  psFormatted: 'n/a',
  analystCount: null,
  growthEstimate: null,
  growthEstimateFormatted: 'n/a',
  earningsGrowth: null,
  earningsGrowthFormatted: 'n/a',
  salesGrowth: null,
  salesGrowthFormatted: 'n/a',
  dividendYield: null,
  dividendYieldFormatted: 'n/a',
  industrySlug: 'n/a',
  industryLabel: 'n/a',
  riskKeys: [],
  rewardKeys: [],
  statements: {},
  hasRisks: false,
  hasRewards: false,
});

/**
 * Risk and reward lists ordered by importance
 */
const rewards = [
  'IsGoodValue',
  'IsGrowingProfitOrRevenue',
  'HasBeenGrowingProfitOrRevenue',
];
const risks = [
  'IsAbleToAchieveProfitability',
  'AreRevenueAndEarningsExpectedToGrow',
  'IsInAGoodFinancialPosition',
  'HasStableSharePriceOverPast3Months',
  'HasNoConcerningRecentEvents',
  'HasSufficientFinancialData',
  'HasHighQualityEarnings',
  'HasNetProfitMarginImprovedOverPastYear',
  'HasMeaningfulRevenue',
  'HasNoSubstantialInsiderSellingOverPastQuarter',
  'HasNotDilutedOverPastYear',
  'HasMeaningfulMarketCap',
  'HasNoNegativeEquity',
];

const getValue = (value: boolean | null): StatementValue => {
  if (value === true) return 'Pass';
  if (value === false) return 'Fail';
  return 'No data';
};

const getFormattedDescription = (description) =>
  description.replace(/\[\[(.*?)\]\]/g, (_matched, original) => {
    return original;
  });

const getSortedRiskKeys = (
  keys: string[],
  entities: Record<string, Statement>
) => {
  return keys.sort((_a, b) => (entities[b].severity === 'major' ? 0 : -1));
};

const statementsMapper = (
  statements: StatementsResponse = { data: [] },
  filter: string[] = []
) => {
  const statementMap = statements.data
    .filter((n) => {
      // No filter, return everything
      if (filter.length === 0) return true;
      // Include risks and rewards
      if (risks.includes(n.name)) return true;
      if (rewards.includes(n.name)) return true;
      // Include filtered statements only
      return filter.includes(n.name);
    })
    .reduce((a, b) => {
      if (typeof a[b.name] === 'undefined') {
        const description = b.description || '';
        a[b.name] = {
          name: b.name,
          title: b.title,
          description,
          formattedDescription: getFormattedDescription(description),
          context: b.context || {},
          value: getValue(b.value),
          severity: b.severity === null ? 'none' : b.severity,
          question: '',
          outcomeName: '',
        };
      }
      return a;
    }, {});
  const passedRewards = rewards.filter(
    (n) => statementMap[n]?.value === 'Pass'
  );
  const failedRisks = risks.filter((n) => statementMap[n]?.value === 'Fail');

  return {
    statements: statementMap,
    riskKeys: getSortedRiskKeys(failedRisks, statementMap),
    rewardKeys: passedRewards,
    hasRisks: failedRisks.length > 0,
    hasRewards: passedRewards.length > 0,
  };
};

export const companyMapper = (
  company: GridResponse,
  statementsFilter: string[] = []
): Company | undefined => {
  try {
    const { score, grid, statements } = company;

    const industryId = grid.data.primary_industry.id;
    let industrySlug = '';
    let industryLabel = '';
    if (industryId !== null && typeof industryId !== 'undefined') {
      try {
        const industry = PRIMARY_INDUSTRIES[industryId.toString()];
        industrySlug = industry.slug || '';
        industryLabel = industry.label;
      } catch (e) {
        console.warn(e);
      }
    }

    const yearFounded = grid.data.year_founded;
    const dividendYield = grid.data.dividend_yield;
    const marketCap = grid.data.market_cap;
    const currencyISO =
      grid.data.currency_info.primary_trading_item_currency_iso;
    const return1D = grid.data.return_1d;
    const return7D = grid.data.return_7d;
    const return1YrAbs = grid.data.return_1yr_abs;
    const analystPriceTarget = grid.data.price_target;
    const bestValuationRatio = getBestValuationRatio(
      grid.data.pb,
      grid.data.pe,
      grid.data.price_to_sales,
      industryLabel
    );

    return {
      ...getDefaultCompany(),
      canonicalUrl: company.canonical_url,
      companyId: company.company_id.toLowerCase(),
      shortDescription: grid.data.description,
      mainThumb: grid.data.main_thumb,
      exchangeSymbol: company.exchange_symbol,
      shortName: company.name,
      legalName: company.name,
      slug: company.slug,
      snowflakePoints: [
        score.data.value,
        score.data.future,
        score.data.past,
        score.data.health,
        score.data.income,
      ].map((score) => score + 1),
      tickerSymbol: company.ticker_symbol,
      uniqueSymbol: company.unique_symbol,
      logoUrl: grid.data.logo_url,
      yearFounded: yearFounded === null ? 'n/a' : yearFounded.toString(),
      return1D: return1D,
      return1DFormatted: formatterPercentage(return1D),
      return7D: return7D,
      return7DFormatted: formatterPercentage(return7D),
      return1YrAbs: return1YrAbs,
      return1YrAbsFormatted: formatterPercentage(return1YrAbs),
      marketCap: marketCap,
      marketCapFormatted: formatterCurrencyTruncated(marketCap, currencyISO, 1),
      sharePrice: grid.data.share_price,
      sharePriceFormatted: formatterCurrency(
        grid.data.share_price,
        currencyISO
      ),
      analystsPriceTarget: analystPriceTarget,
      analystsPriceTargetFormatted: formatterCurrency(
        analystPriceTarget,
        currencyISO
      ),
      bestValuationRatio,
      peFormatted: formatterFactor(grid.data.pe),
      pbFormatted: formatterFactor(grid.data.pb),
      psFormatted: formatterFactor(grid.data.price_to_sales),
      analystCount: grid.data.analyst_count,
      growthEstimate: grid.data.growth_3y,
      growthEstimateFormatted: formatterPercentage(grid.data.growth_3y),
      earningsGrowth: grid.data.net_income_growth_annual,
      earningsGrowthFormatted: formatterPercentage(
        grid.data.net_income_growth_annual
      ),
      salesGrowth: grid.data.revenue_growth_annual,
      salesGrowthFormatted: formatterPercentage(
        grid.data.revenue_growth_annual
      ),
      dividendYield: dividendYield,
      dividendYieldFormatted: formatterPercentage(dividendYield),
      industrySlug,
      industryLabel,
      ...statementsMapper(statements, statementsFilter),
    };
  } catch (e) {
    console.warn(e);
  }
};

export const getQueryKey = (
  rules = '',
  pageQuery: number,
  isInfinite = false
): [string, number | string] => [rules, isInfinite ? 1 : pageQuery];
